import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { IntlContext } from "context";
import { ArrowLeft, Dot, ExternalLink, MapPin, MoreVertical, RefreshCw, Timer, Users } from "lucide-react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { useAd, useUpdateAd } from "hooks/ad";
import { AdFrequency } from "types/ad";
import { SUBCATEGORY_TO_CATEGORY, SubCategory } from "types/subCategory";
import { buildRoute, ROUTES } from "routes";
import { formatAdServiceLocation } from "helpers/formatAdServiceLocation";
import type { ServiceInterface } from "types/service";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Loader } from "ui/Loader";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { fetchOurServices, serviceKeys } from "hooks/service";
import { ProviderListCard } from "app/OurService/List/ProviderListCard";
import { getCategoryStyle } from "../../MyServices/categoryStyle";

const MyAdDetails: React.FC = () => {
    const intl = useContext(IntlContext);
    const { id } = useParams();
    const { data: ad, isLoading } = useAd(id);
    const updateAdMutation = useUpdateAd();

    const { data: suggestedServices = [], isLoading: loadingSuggestions } = useQuery({
        queryKey: [...serviceKeys.ourLists(), "suggested-for-ad", String(ad?.id ?? "__missing__")],
        enabled: Boolean(ad),
        queryFn: async () => {
            if (!ad) return [];
            const response = await fetchOurServices({
                filter_categories: String(ad.category),
                filter_subcategories: String(ad.subcategory),
                filter_min_rating: "4",
                per_page: 60,
            });
            return response as unknown as ServiceInterface[];
        },
    });

    const handleToggleActive = async () => {
        try {
            await updateAdMutation.mutateAsync({
                id: ad?.id ?? "",
                data: { ...ad, is_active: !ad?.is_active } as unknown as HashMap<unknown>,
            });
        } catch {
            // Keep UI unchanged on failure.
        }
    };

    if (isLoading || !ad) {
        return <Loader />;
    }

    const categoryKey = ad.category.toString();
    const { Icon, boxClass, iconClass } = getCategoryStyle(categoryKey);
    const subCategory = ad.subcategory as SubCategory | undefined;

    const subcategoryLabel = subCategory ? intl.formatMessage(`subcategory.${SUBCATEGORY_TO_CATEGORY[subCategory].toLocaleLowerCase()}.${subCategory.toLocaleLowerCase()}.label`) : "";

    const title =
        ad.title?.trim() ||
        (subCategory
            ? intl.formatMessage(
                  `subcategory.${SUBCATEGORY_TO_CATEGORY[subCategory].toLocaleLowerCase()}.${subCategory.toLocaleLowerCase()}.label`
              )
            : intl.formatMessage(`category.${categoryKey.toLocaleLowerCase()}`));

    const location = formatAdServiceLocation(ad);
    const applicationsCount = ad.providers_apply?.length ?? 0;
    const isRecurring = ad.frequency === AdFrequency.RECURRING;
    const publishedLabel = intl.formatDate(ad.created_at, {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    const statusBadge = ad.is_active ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {intl.formatMessage("ad.card.status_active")}
        </span>
    ) : (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
            {intl.formatMessage("ad.card.status_closed")}
        </span>
    );

    return (
        <div className="space-y-6 mx-16 my-12">
            <Link
                to={ROUTES.MY_ADS}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-primary"
            >
                <ArrowLeft className="h-4 w-4" strokeWidth={2.25} />
                {intl.formatMessage("profile.my_ads.back")}
            </Link>

            <article className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 items-start gap-4">
                        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${boxClass}`}>
                            <Icon className={`h-5 w-5 ${iconClass}`} strokeWidth={2} />
                        </div>

                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-lg font-extrabold text-gray-900">{title}</h1>
                                <Badge
                                    variant="frequency"
                                    palette={isRecurring ? "violet" : "orange"}
                                    className="normal-case tracking-normal"
                                >
                                    {isRecurring ? <RefreshCw /> : <Timer />}
                                    {isRecurring
                                        ? intl.formatMessage("ad.card.frequency_recurring")
                                        : intl.formatMessage("ad.card.frequency_one_time")}
                                </Badge>
                                {statusBadge}
                            </div>

                            <div className="mt-2 flex flex-wrap items-center text-sm text-slate-500">
                                <span>{subcategoryLabel}</span>
                                {subcategoryLabel && <Dot className="h-4 w-4" />}
                                <span className="inline-flex items-center gap-1">
                                    <MapPin className="h-4 w-4 shrink-0" />
                                    <span className="truncate">{location}</span>
                                </span>
                                <Dot className="h-4 w-4" />
                                <span>
                                    {intl.formatMessage("profile.my_ads.details.published_on", {
                                        date: publishedLabel,
                                    })}
                                </span>
                            </div>
                        </div>
                    </div>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 rounded-lg">
                                <MoreVertical className="h-4 w-4" />
                                <span className="sr-only">{intl.formatMessage("ad.card.actions")}</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl">
                            <DropdownMenuItem asChild>
                                <Link to={buildRoute.myAdEdit(ad.id)}>{intl.formatMessage("ad.card.edit")}</Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => void handleToggleActive()}>
                                {ad.is_active
                                    ? intl.formatMessage("ad.label.disable")
                                    : intl.formatMessage("ad.label.enable")}
                            </DropdownMenuItem>
                            {applicationsCount > 0 && (
                                <DropdownMenuItem asChild>
                                    <Link to={buildRoute.myAdApplications(ad.id)}>
                                        <Users className="mr-2 h-4 w-4" />
                                        {intl.formatMessage("ad.card.applications", { count: applicationsCount })}
                                    </Link>
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild>
                                <Link to={buildRoute.ad(ad.id)} target="_blank">
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    {intl.formatMessage("ad.card.view_public_ad")}
                                </Link>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="mt-4 rounded-2xl border border-gray-200 bg-slate-50 p-4 text-sm text-slate-600">
                    {ad.description}
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-3">
                        <div className="text-2xl font-bold leading-none text-gray-900">{applicationsCount}</div>
                        <div className="text-xs text-slate-500">{intl.formatMessage("ad.card.candidates")}</div>
                    </div>

                    {applicationsCount > 0 && (
                        <Button variant="outline" size="sm" className="rounded-xl" asChild>
                            <Link to={buildRoute.myAdApplications(ad.id)}>
                                <Users className="h-4 w-4" />
                                {intl.formatMessage("ad.card.applications", { count: applicationsCount })}
                            </Link>
                        </Button>
                    )}
                </div>
            </article>

            <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                        <Users className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-base font-extrabold text-gray-900">
                            {intl.formatMessage("profile.my_ads.details.suggested_providers_title")}
                        </h2>
                        <p className="mt-0.5 text-sm text-slate-500">
                            {loadingSuggestions
                                ? intl.formatMessage("profile.my_ads.details.suggested_providers_loading")
                                : suggestedServices.length > 0
                                  ? intl.formatMessage("profile.my_ads.details.suggested_providers_match", {
                                        count: suggestedServices.length,
                                    })
                                  : intl.formatMessage("profile.my_ads.details.suggested_providers_empty")}
                        </p>
                    </div>
                </div>

                {!loadingSuggestions && suggestedServices.length > 0 && (
                    <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {suggestedServices.map((service) => (
                            <ProviderListCard key={service.id} service={service} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default MyAdDetails;

